import React, { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminPagination, {
  AdminSearchRow,
} from '../../components/admin/AdminPagination';
import { ConfirmModal } from '../../components/dicionarios';
import useAdminList from '../../hooks/useAdminList';
import { revalidateCadastroOperacional } from '../../hooks/clearCache';
import * as adminAtividades from '../../services/adminAtividades';
import handleFieldChange from '../../components/admin/formHelpers';

const emptyForm = {
  codProcedi: '',
  codConv: '',
  qtdCh: '',
  vlrCh: '',
  grupo: '',
  vlrReal: '',
};

export default function Atividades() {
  const list = useAdminList('admin-atividades', adminAtividades.listAtividades);
  const [formData, setFormData] = useState(emptyForm);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectItem = index => {
    const item = list.items[index];
    if (!item) return;
    setSelectedIndex(index);
    setFormData({
      codProcedi: item.codProcedi ?? '',
      codConv: item.codConv ?? '',
      qtdCh: item.qtdCh ?? '',
      vlrCh: item.vlrCh ?? '',
      grupo: item.grupo ?? '',
      vlrReal: item.vlrReal ?? '',
    });
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleEdit = () => {
    if (!list.items.length) {
      toast.warn('Nenhum registro para editar!');
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (list.items.length) selectItem(Math.min(selectedIndex, list.items.length - 1));
    else setFormData(emptyForm);
  };

  const buildPayload = () => ({
    codProcedi: Number(formData.codProcedi),
    codConv: Number(formData.codConv),
    qtdCh: formData.qtdCh === '' ? null : Number(formData.qtdCh),
    vlrCh: formData.vlrCh === '' ? null : Number(formData.vlrCh),
    grupo: formData.grupo || null,
    vlrReal: formData.vlrReal === '' ? null : Number(formData.vlrReal),
  });

  const handleSave = async () => {
    if (!formData.codProcedi || !formData.codConv) {
      toast.warn('Convênio e procedimento são obrigatórios!');
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (isCreating) {
        await adminAtividades.createAtividade(payload);
        toast.success('Atividade criada!');
      } else {
        await adminAtividades.updateAtividade(
          Number(formData.codProcedi),
          Number(formData.codConv),
          payload
        );
        toast.success('Atividade atualizada!');
      }
      setIsCreating(false);
      setIsEditing(false);
      await list.mutate();
      revalidateCadastroOperacional();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar atividade!');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminAtividades.deleteAtividade(
        Number(formData.codProcedi),
        Number(formData.codConv)
      );
      toast.success('Atividade excluída!');
      setShowDeleteConfirm(false);
      await list.mutate();
      revalidateCadastroOperacional();
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao excluir atividade!');
    }
  };

  return (
    <AdminPageShell
      title="Cadastro de Atividades"
      subtitle="Tabela de preços por convênio e procedimento"
    >
      <AdminSearchRow
        q={list.q}
        onChange={list.setQ}
        placeholder="Buscar convênio, procedimento ou grupo"
      />

      {list.isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Convênio</th>
                  <th>Procedimento</th>
                  <th>Qtde CH</th>
                  <th>Valor CH</th>
                  <th>Grupo</th>
                  <th>Valor (R$)</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item, index) => (
                  <tr
                    key={`${item.codProcedi}-${item.codConv}`}
                    onClick={() => selectItem(index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        index === selectedIndex && !isCreating
                          ? '#e2f0f0'
                          : undefined,
                    }}
                  >
                    <td>
                      {item.siglaConv} ({item.codConv})
                    </td>
                    <td>
                      {item.descProcedi} ({item.codProcedi})
                    </td>
                    <td>{item.qtdCh}</td>
                    <td>{item.vlrCh}</td>
                    <td>{item.grupo}</td>
                    <td>{item.vlrReal}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {!list.items.length && (
            <Alert variant="light" className="text-center">
              Nenhuma atividade encontrada.
            </Alert>
          )}
          <AdminPagination
            page={list.page}
            total={list.total}
            limit={list.limit}
            onPageChange={list.setPage}
          />
        </>
      )}

      <hr />
      <Form>
        <Row>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Cód. Proced.</Form.Label>
              <Form.Control
                value={formData.codProcedi}
                disabled={!isCreating}
                onChange={handleFieldChange(setFormData, 'codProcedi')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Cód. Conv.</Form.Label>
              <Form.Control
                value={formData.codConv}
                disabled={!isCreating}
                onChange={handleFieldChange(setFormData, 'codConv')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Qtde CH</Form.Label>
              <Form.Control
                type="number"
                value={formData.qtdCh}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'qtdCh')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Valor CH</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.vlrCh}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'vlrCh')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Grupo</Form.Label>
              <Form.Control
                value={formData.grupo}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'grupo')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Valor (R$)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.vlrReal}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'vlrReal')}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <div className="d-flex flex-wrap mt-3">
        <Button variant="primary" className="mr-2 mb-2" onClick={handleNew} disabled={isEditing}>
          Novo
        </Button>
        <Button variant="secondary" className="mr-2 mb-2" onClick={handleEdit} disabled={isEditing || !list.items.length}>
          Editar
        </Button>
        <Button variant="success" className="mr-2 mb-2" onClick={handleSave} disabled={!isEditing || isSaving}>
          {isSaving ? 'Gravando...' : 'Gravar'}
        </Button>
        <Button variant="warning" className="mr-2 mb-2" onClick={handleCancel} disabled={!isEditing}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          className="mb-2"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isCreating || !formData.codProcedi || isEditing}
        >
          Excluir
        </Button>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Excluir atividade"
        message="Confirma a exclusão deste preço?"
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
    </AdminPageShell>
  );
}
